import React, { useEffect, useRef, useState, useCallback } from 'react';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import Draw from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import GeoJSON from 'ol/format/GeoJSON';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Style, Fill, Stroke } from 'ol/style';

const geojsonFormat = new GeoJSON({
  dataProjection: 'EPSG:4326',
  featureProjection: 'EPSG:3857',
});

const editableStyle = new Style({
  fill: new Fill({ color: 'rgba(99, 8, 1, 0.2)' }),
  stroke: new Stroke({ color: '#630801', width: 2 }),
});

const referenceStyle = new Style({
  fill: new Fill({ color: 'rgba(100, 100, 100, 0.1)' }),
  stroke: new Stroke({ color: '#999', width: 1, lineDash: [4, 4] }),
});

export default function AppellationMapEditor({ boundary, onBoundaryChange, referenceBoundaries = [] }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const editSourceRef = useRef(null);
  const referenceSourceRef = useRef(null);
  const drawInteractionRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const fileInputRef = useRef(null);

  const syncBoundary = useCallback(() => {
    if (!editSourceRef.current) return;
    const features = editSourceRef.current.getFeatures();
    if (features.length === 0) {
      onBoundaryChange(null);
      return;
    }

    // Merge all features into a single MultiPolygon
    const allCoords = [];
    for (const feature of features) {
      const geom = feature.getGeometry();
      if (!geom) continue;
      const geojson = JSON.parse(geojsonFormat.writeGeometry(geom));
      if (geojson.type === 'Polygon') {
        allCoords.push(geojson.coordinates);
      } else if (geojson.type === 'MultiPolygon') {
        allCoords.push(...geojson.coordinates);
      }
    }

    if (allCoords.length === 0) {
      onBoundaryChange(null);
      return;
    }

    onBoundaryChange({
      type: 'MultiPolygon',
      coordinates: allCoords,
    });
  }, [onBoundaryChange]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const editSource = new VectorSource();
    editSourceRef.current = editSource;

    const referenceSource = new VectorSource();
    referenceSourceRef.current = referenceSource;

    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new XYZ({
            url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
            attributions: '&copy; OpenStreetMap contributors',
          }),
        }),
        new VectorLayer({
          source: referenceSource,
          style: referenceStyle,
        }),
        new VectorLayer({
          source: editSource,
          style: editableStyle,
        }),
      ],
      view: new View({
        center: fromLonLat([-98.5, 39.8]), // Center of US
        zoom: 4,
      }),
    });

    // Add modify interaction
    const modify = new Modify({ source: editSource });
    modify.on('modifyend', () => syncBoundary());
    map.addInteraction(modify);

    mapInstance.current = map;

    // Load existing boundary
    if (boundary) {
      try {
        const features = geojsonFormat.readFeatures(boundary);
        editSource.addFeatures(features);
        if (features.length > 0) {
          const extent = editSource.getExtent();
          map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 12 });
        }
      } catch (e) {
        console.error('Error loading boundary:', e);
      }
    }

    return () => {
      map.setTarget(null);
      mapInstance.current = null;
    };
  }, []); // eslint-disable-line

  // Update reference boundaries
  useEffect(() => {
    if (!referenceSourceRef.current) return;
    referenceSourceRef.current.clear();
    for (const ref of referenceBoundaries) {
      if (!ref.boundary) continue;
      try {
        const features = geojsonFormat.readFeatures(ref.boundary);
        referenceSourceRef.current.addFeatures(features);
      } catch {
        // Skip invalid geometries
      }
    }
  }, [referenceBoundaries]);

  const startDrawing = () => {
    if (!mapInstance.current || isDrawing) return;
    const draw = new Draw({
      source: editSourceRef.current,
      type: 'Polygon',
    });
    draw.on('drawend', () => {
      // Small timeout to let the feature be added first
      setTimeout(() => {
        syncBoundary();
        stopDrawing();
      }, 100);
    });
    mapInstance.current.addInteraction(draw);
    drawInteractionRef.current = draw;
    setIsDrawing(true);
  };

  const stopDrawing = () => {
    if (!mapInstance.current || !drawInteractionRef.current) return;
    mapInstance.current.removeInteraction(drawInteractionRef.current);
    drawInteractionRef.current = null;
    setIsDrawing(false);
  };

  const clearAll = () => {
    if (!editSourceRef.current) return;
    editSourceRef.current.clear();
    onBoundaryChange(null);
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const geojson = JSON.parse(evt.target.result);
        const features = geojsonFormat.readFeatures(geojson);
        if (features.length === 0) {
          alert('No polygon features found in file.');
          return;
        }
        editSourceRef.current.clear();
        editSourceRef.current.addFeatures(features);

        // Zoom to imported features
        const extent = editSourceRef.current.getExtent();
        mapInstance.current.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 12 });

        syncBoundary();
      } catch (err) {
        console.error('Error importing GeoJSON:', err);
        alert('Failed to parse GeoJSON file.');
      }
    };
    reader.readAsText(file);
    // Reset file input so same file can be re-selected
    e.target.value = '';
  };

  const exportGeoJSON = () => {
    if (!editSourceRef.current) return;
    const features = editSourceRef.current.getFeatures();
    if (features.length === 0) {
      alert('No features to export.');
      return;
    }
    const geojson = geojsonFormat.writeFeaturesObject(features);
    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'appellation-boundary.geojson';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <button
          type="button"
          onClick={isDrawing ? stopDrawing : startDrawing}
          className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
            isDrawing
              ? 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          {isDrawing ? 'Stop Drawing' : 'Draw Polygon'}
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="px-3 py-1.5 text-xs font-medium rounded-md border bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
        >
          Import GeoJSON
        </button>
        <button
          type="button"
          onClick={exportGeoJSON}
          className="px-3 py-1.5 text-xs font-medium rounded-md border bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
        >
          Export GeoJSON
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="px-3 py-1.5 text-xs font-medium rounded-md border bg-white text-red-600 border-red-300 hover:bg-red-50"
        >
          Clear
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileImport}
          accept=".geojson,.json"
          className="hidden"
        />
      </div>

      {/* Map Container */}
      <div
        ref={mapRef}
        className="w-full rounded-md border border-gray-300"
        style={{ height: '500px' }}
      />

      <p className="mt-2 text-xs text-gray-500">
        Click &quot;Draw Polygon&quot; to draw a boundary, or import a GeoJSON file. Click on vertices to edit. Gray dashed boundaries show sibling appellations for reference.
      </p>
    </div>
  );
}
